import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './History.css';

const STATUS_MAP = {
  0: { label: 'Pending', className: 'status-pending' },
  1: { label: 'Active', className: 'status-active' },
  2: { label: 'Used', className: 'status-used' },
  3: { label: 'Finished', className: 'status-finished' },
  4: { label: 'Unused', className: 'status-unused' },
  5: { label: 'Failed', className: 'status-failed' },
};

// Order status enum from backend: NEW=0, COMPLETED=1, FAILED=2
const PAYMENT_STATUS_MAP = {
  0: { label: 'Pending', className: 'status-pending' },
  1: { label: 'Completed', className: 'status-finished' },
  2: { label: 'Failed', className: 'status-failed' },
};

// ProductService enum from backend: GOOGLE=0, NOW_PAYMENTS=1, CRYPTOMUS=2
const PAYMENT_SERVICE_MAP = {
  0: { label: 'Google Play', icon: 'shop' },
  1: { label: 'NOWPayments', icon: 'currency_bitcoin' },
  2: { label: 'Cryptomus', icon: 'currency_bitcoin' },
};

const History = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [rentals, setRentals] = useState([]);
  const [longTermRentals, setLongTermRentals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState(() => searchParams.get('tab') === 'payments' ? 'payments' : 'short');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth');
      return;
    }

    const fetchHistory = async () => {
      try {
        const [shortRes, longRes, paymentsRes] = await Promise.allSettled([
          api.get('/v1/users/rentals', true),
          api.get('/v1/users/long-term-rentals/my-rentals', true),
          api.get('/v1/payments/history', true),
        ]);
        if (shortRes.status === 'fulfilled') {
          const shortList = shortRes.value?.data?.userRentalData || shortRes.value?.data?.userRentalHistory || [];
          setRentals(Array.isArray(shortList) ? shortList : []);
        }
        if (longRes.status === 'fulfilled') {
          const longList = longRes.value?.data?.rentals || [];
          setLongTermRentals(longList.map(r => ({ ...r, _isLongTerm: true })));
        }
        if (paymentsRes.status === 'fulfilled') {
          const paymentList = paymentsRes.value?.data?.history || [];
          setPayments(Array.isArray(paymentList) ? paymentList : []);
        }
      } catch (err) {
        console.error('Failed to fetch rental history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const getStatus = (statusCode) => STATUS_MAP[statusCode] || { label: 'Unknown', className: 'status-unknown' };

  const isRentalActive = (rental) => {
    if (rental._isLongTerm) {
      return rental.status === 1 && !rental.isExpired;
    }
    return (rental.status === 1 || rental.status === 2) && new Date(rental.finishedAt) > new Date();
  };

  const getTimeLeft = (rental) => {
    if (rental._isLongTerm) {
      const remaining = new Date(rental.expiresAt).getTime() - Date.now();
      if (remaining <= 0) return null;
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      if (days > 0) return `${days}d ${hours}h`;
      const minutes = Math.floor((remaining % 3600000) / 60000);
      return `${hours}h ${minutes}m`;
    }
    const remaining = new Date(rental.finishedAt).getTime() - Date.now();
    if (remaining <= 0) return null;
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const currentRentals = tab === 'short' ? rentals : longTermRentals;

  const filteredRentals = currentRentals.filter((rental) => {
    if (filter === 'all') return true;
    if (filter === 'active') return isRentalActive(rental);
    if (filter === 'completed') return [2, 3].includes(rental.status);
    if (filter === 'canceled') return [4, 5].includes(rental.status);
    return true;
  });

  const handleViewRental = (rental) => {
    if (!isRentalActive(rental)) return;

    if (rental._isLongTerm) {
      navigate('/verification', {
        state: {
          rental: {
            data: {
              rental: { ...rental },
              messages: [],
              userBalance: null,
            },
          },
          countryFlagUrl: rental.countryFlagUrl,
        },
      });
    } else {
      navigate('/verification', {
        state: {
          rental: {
            data: {
              userRentalData: { ...rental, availableUntil: new Date(rental.finishedAt).getTime() - Date.now() },
              messages: [],
              userBalance: null,
            },
          },
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="history-container">
          <div className="history-loading">
            <div className="history-spinner"></div>
            <p>Loading rental history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-container">
        <div className="history-header">
          <div>
            <h1 className="history-title">My Numbers</h1>
            <p className="history-subtitle">View your rented numbers and rental history</p>
          </div>
          <button className="history-get-number-btn" onClick={() => navigate('/services')}>
            <span className="material-symbols-outlined">add</span>
            <span>Get New Number</span>
          </button>
        </div>

        {/* Type Tabs */}
        <div className="history-type-tabs">
          <button
            className={`history-type-tab ${tab === 'short' ? 'active' : ''}`}
            onClick={() => { setTab('short'); setFilter('all'); }}
          >
            <span className="material-symbols-outlined">timer</span>
            Short Term
            {rentals.filter(r => isRentalActive(r)).length > 0 && (
              <span className="filter-count">{rentals.filter(r => isRentalActive(r)).length}</span>
            )}
          </button>
          <button
            className={`history-type-tab ${tab === 'long' ? 'active' : ''}`}
            onClick={() => { setTab('long'); setFilter('all'); }}
          >
            <span className="material-symbols-outlined">calendar_month</span>
            Long Term
            {longTermRentals.filter(r => isRentalActive(r)).length > 0 && (
              <span className="filter-count">{longTermRentals.filter(r => isRentalActive(r)).length}</span>
            )}
          </button>
          <button
            className={`history-type-tab ${tab === 'payments' ? 'active' : ''}`}
            onClick={() => { setTab('payments'); setFilter('all'); }}
          >
            <span className="material-symbols-outlined">receipt_long</span>
            Payments
            {payments.length > 0 && (
              <span className="filter-count">{payments.length}</span>
            )}
          </button>
        </div>

        {/* Filter Tabs */}
        {tab === 'payments' ? (
          <div className="history-filters">
            {['all', 'completed', 'failed', 'pending'].map((f) => (
              <button
                key={f}
                className={`history-filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        ) : (
          <div className="history-filters">
            {['all', 'active', 'completed', 'canceled'].map((f) => (
              <button
                key={f}
                className={`history-filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'active' && (
                  <span className="filter-count">
                    {currentRentals.filter((r) => isRentalActive(r)).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Payments List */}
        {tab === 'payments' ? (
          (() => {
            const filteredPayments = payments.filter((p) => {
              if (filter === 'all') return true;
              if (filter === 'completed') return p.status === 1;
              if (filter === 'failed') return p.status === 2;
              if (filter === 'pending') return p.status === 0;
              return true;
            });

            if (filteredPayments.length === 0) {
              return (
                <div className="history-empty">
                  <span className="material-symbols-outlined history-empty-icon">receipt_long</span>
                  <p className="history-empty-title">
                    {filter === 'all' ? 'No payments yet' : `No ${filter} payments`}
                  </p>
                  <p className="history-empty-desc">
                    {filter === 'all'
                      ? 'Your coin purchases will appear here'
                      : 'Try a different filter to see your payments'}
                  </p>
                  {filter === 'all' && (
                    <button className="history-get-number-btn" onClick={() => navigate('/checkout')}>
                      <span className="material-symbols-outlined">add</span>
                      <span>Add Funds</span>
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div className="history-list">
                {filteredPayments.map((payment) => {
                  const status = PAYMENT_STATUS_MAP[payment.status] || { label: 'Unknown', className: 'status-unknown' };
                  const service = PAYMENT_SERVICE_MAP[payment.service] || { label: 'Unknown', icon: 'payments' };
                  return (
                    <div key={payment._id} className="history-item">
                      <div className="history-item-left">
                        <div className="history-item-icon">
                          <span className="material-symbols-outlined">{service.icon}</span>
                        </div>
                        <div className="history-item-info">
                          <p className="history-item-phone">{service.label}</p>
                          <p className="history-item-service">
                            +{payment.coins.toLocaleString()} coins added
                          </p>
                          <p className="history-item-date">
                            {new Date(payment.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="history-item-right">
                        <div className="history-item-meta">
                          <span className={`history-status ${status.className}`}>
                            <span className="history-status-dot"></span>
                            {status.label}
                          </span>
                        </div>
                        <p className="history-item-price">
                          Paid ${Number(payment.paidAmount || payment.price).toFixed(2)} {payment.currency}
                        </p>
                        {payment.paidAmount > 0 && Number(payment.paidAmount) !== Number(payment.price) && (
                          <p className="history-item-date">
                            ordered ${Number(payment.price).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : /* Rentals List */
        filteredRentals.length === 0 ? (
          <div className="history-empty">
            <span className="material-symbols-outlined history-empty-icon">
              {filter === 'all' ? 'phone_disabled' : 'filter_list_off'}
            </span>
            <p className="history-empty-title">
              {filter === 'all' ? 'No rentals yet' : `No ${filter} rentals`}
            </p>
            <p className="history-empty-desc">
              {filter === 'all'
                ? 'Get your first number to start receiving SMS'
                : 'Try a different filter to see your rentals'}
            </p>
            {filter === 'all' && (
              <button className="history-get-number-btn" onClick={() => navigate('/services')}>
                <span className="material-symbols-outlined">add</span>
                <span>Get Number</span>
              </button>
            )}
          </div>
        ) : (
          <div className="history-list">
            {filteredRentals.map((rental) => {
              const status = getStatus(rental.status);
              const active = isRentalActive(rental);
              const timeLeft = active ? getTimeLeft(rental) : null;

              return (
                <div
                  key={rental._id}
                  className={`history-item ${active ? 'active' : ''}`}
                  onClick={() => handleViewRental(rental)}
                  style={{ cursor: active ? 'pointer' : 'default' }}
                >
                  <div className="history-item-left">
                    <div className="history-item-icon">
                      {rental._isLongTerm && rental.countryFlagUrl ? (
                        <img src={rental.countryFlagUrl} alt={rental.countryCode} className="history-item-flag" />
                      ) : (
                        <span className="material-symbols-outlined">
                          {active ? 'phone_in_talk' : 'phone'}
                        </span>
                      )}
                    </div>
                    <div className="history-item-info">
                      <p className="history-item-phone">
                        {rental.phoneNumber ? `+${rental.phoneNumber}` : 'Number not assigned'}
                      </p>
                      <p className="history-item-service">
                        {rental._isLongTerm
                          ? `Long Term · ${rental.rentalDurationMonths} month${rental.rentalDurationMonths > 1 ? 's' : ''}`
                          : (rental.serviceName || 'Unknown service').toUpperCase()}
                      </p>
                      <p className="history-item-date">
                        {rental._isLongTerm
                          ? `Rented ${new Date(rental.rentedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · Expires ${new Date(rental.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : new Date(rental.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                      </p>
                    </div>
                  </div>
                  <div className="history-item-right">
                    <div className="history-item-meta">
                      <span className={`history-status ${status.className}`}>
                        <span className="history-status-dot"></span>
                        {status.label}
                      </span>
                      {timeLeft && (
                        <span className="history-timer">
                          <span className="material-symbols-outlined">
                            {rental._isLongTerm ? 'calendar_month' : 'timer'}
                          </span>
                          {rental._isLongTerm ? `${rental.remainingDays}d left` : timeLeft}
                        </span>
                      )}
                    </div>
                    <p className="history-item-price">
                      {rental._isLongTerm
                        ? `${(rental.coinsSpent || 0).toLocaleString()} coins`
                        : `$${(rental.coins / 100).toFixed(2)}`}
                    </p>
                    {active && (
                      <span className="material-symbols-outlined history-item-chevron">chevron_right</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
